import {
  Children,
  cloneElement,
  isValidElement,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import classNames from 'classnames';
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi';
import useIsMounted from 'src/hooks/useIsMounted';
import { twMerge } from 'src/helpers';

type ResponsiveSettings = Partial<
  Pick<
    Props,
    | 'perSlide'
    | 'infinite'
    | 'hasBlankCard'
    | 'scrollContainerClassName'
    | 'btnContainerClassName'
    | 'isArrowBottom'
    | 'centerMode'
    | 'centerPadding'
  >
>;

type ResponsiveBreakpoint = {
  breakpoint: number;
  settings: ResponsiveSettings;
};

type Props = {
  totalCount: number;
  numRecord: number;
  perSlide?: number;
  renderTitle?: () => JSX.Element;
  infinite?: boolean;
  hasBlankCard?: boolean;
  scrollContainerClassName?: string;
  btnContainerClassName?: string;
  isArrowBottom?: boolean;
  centerMode?: boolean;
  centerPadding?: string | number;
  initialIndex?: number;
  responsive?: ResponsiveBreakpoint[];
};

type Direction = -1 | 1;
const IDLE_FRAMES = 3;
const RUNWAY_SLIDES = 30;
const OVERSCAN_SLIDES = 2;
const SNAP_TYPE = 'x proximity';
const RESCUE_RATIO = 1 / 3;

type ScrollData = {
  index: number;
  containerWidth: number;
  cardWidth: number;
  numOfScroll: number;
  sliding: boolean;
};

const parseCenterPadding = (
  centerPadding: string | number | undefined,
  containerWidth: number
) => {
  if (typeof centerPadding === 'number') return centerPadding;

  const value = centerPadding || '0px';
  const parsedValue = Number.parseFloat(value);
  if (!Number.isFinite(parsedValue)) return 0;

  return value.trim().endsWith('%') ? (containerWidth * parsedValue) / 100 : parsedValue;
};

const getActiveResponsiveSettings = (
  responsive: ResponsiveBreakpoint[] | undefined,
  windowWidth: number
): ResponsiveSettings => {
  if (!responsive || responsive.length === 0) return {};

  const sorted = [...responsive].sort((a, b) => a.breakpoint - b.breakpoint);
  const matched = sorted.find(({ breakpoint }) => windowWidth <= breakpoint);
  return matched?.settings ?? {};
};

const useResponsiveSettings = (responsive: ResponsiveBreakpoint[] | undefined) => {
  const isMounted = useIsMounted();
  const [windowWidth, setWindowWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!responsive || responsive.length === 0) return;

    const updateWidth = () => setWindowWidth(window.innerWidth);
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [responsive]);

  return useMemo(
    () => (isMounted && windowWidth !== null ? getActiveResponsiveSettings(responsive, windowWidth) : {}),
    [isMounted, responsive, windowWidth]
  );
};

const applyCardSize = (card: HTMLElement, cardWidth: number) => {
  card.style.width = `${cardWidth}px`;
  card.style.minWidth = `${cardWidth}px`;
  card.style.maxWidth = `${cardWidth}px`;
  card.style.flex = `0 0 ${cardWidth}px`;
  card.style.scrollSnapAlign = 'start';

  const cardContent = card.firstElementChild as HTMLElement | null;
  if (cardContent) {
    cardContent.style.width = '100%';
    cardContent.style.minWidth = '100%';
    cardContent.style.maxWidth = '100%';
    cardContent.style.flexBasis = '100%';
  }
};

const ArrowButtons: React.FC<{
  disabledPrev: boolean;
  disabledNext: boolean;
  className?: string;
  onMove: (direction: Direction) => void;
}> = ({ disabledPrev, disabledNext, className, onMove }) => (
  <div className={twMerge('tw-hidden md:tw-flex tw-items-center', className)}>
    <div className="tw-flex tw-items-center">
      <div
        className={classNames('tw-inline-block tw-text-center tw-mr-2', {
          'tw-cursor-pointer tw-text-brand-navy': !disabledPrev,
          'tw-text-grey-lighter': disabledPrev,
        })}
        onClick={() => onMove(-1)}
      >
        <HiOutlineArrowNarrowLeft size={28} strokeWidth={2} />
      </div>
      <div className="tw-h-8 tw-border-l-2 tw-border-brand-navy tw-transform tw-rotate-45 tw-mx-4" />
      <div
        className={classNames('tw-inline-block tw-text-center', {
          'tw-cursor-pointer tw-text-brand-navy': !disabledNext,
          'tw-text-grey-lighter': disabledNext,
        })}
        onClick={() => onMove(1)}
      >
        <HiOutlineArrowNarrowRight size={28} strokeWidth={2} />
      </div>
    </div>
  </div>
);

const SectionLayout: React.FC<{
  children: ReactNode;
  renderTitle?: () => JSX.Element;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  containerClassName: string;
  containerStyle?: React.CSSProperties;
  showArrowButtons: boolean;
  isArrowBottom?: boolean;
  arrowButtons: ReactNode;
}> = ({
  children,
  renderTitle,
  containerRef,
  containerClassName,
  containerStyle,
  showArrowButtons,
  isArrowBottom,
  arrowButtons,
}) => (
  <>
    <div className="tw-flex tw-items-center tw-justify-between tw-relative">
      {renderTitle && <div className="tw-w-full md:tw-w-auto tw-flex-grow tw-mb-8">{renderTitle()}</div>}
      {showArrowButtons && !isArrowBottom ? arrowButtons : null}
    </div>

    <div ref={containerRef} className={containerClassName} style={containerStyle}>
      {children}
    </div>

    {showArrowButtons && isArrowBottom ? arrowButtons : null}
  </>
);

const StandardArrowButtons: React.FC<{
  numOfCards: number;
  perSlide: number;
  className?: string;
  infinite?: boolean;
  scrollDataRef: MutableRefObject<ScrollData | undefined>;
  containerRef: MutableRefObject<HTMLDivElement | null>;
}> = ({ numOfCards, perSlide, className, scrollDataRef, containerRef, infinite }) => {
  const [disabledPrev, setDisabledPrev] = useState(!infinite);
  const [disabledNext, setDisabledNext] = useState(false);

  const scroll = () => {
    const scrollData = scrollDataRef.current;
    const container = containerRef.current;
    if (!scrollData || !container) return;

    const lastNumOfCards = numOfCards % perSlide;
    const prevIndex = Math.max(scrollData.index - 1, 0);
    const isScrollToEnd = scrollData.index + 1 === scrollData.numOfScroll && lastNumOfCards === 0;
    const distance =
      scrollData.index + 1 < scrollData.numOfScroll || isScrollToEnd
        ? scrollData.index * scrollData.containerWidth
        : prevIndex * scrollData.containerWidth + lastNumOfCards * scrollData.cardWidth;

    container.scroll({ left: distance, behavior: 'smooth' });
    const currentValues = scrollData;
    setTimeout(() => {
      scrollDataRef.current = { ...currentValues, sliding: false };
    }, 200);
  };

  const move = (direction: Direction) => {
    const scrollData = scrollDataRef.current;
    if (!scrollData || scrollData.sliding) return;

    const nextIndex = scrollData.index + direction;
    if (nextIndex >= 0 && nextIndex < scrollData.numOfScroll) {
      scrollDataRef.current = { ...scrollData, index: nextIndex, sliding: true };

      if (!infinite && nextIndex === 0) setDisabledPrev(true);
      if (!infinite && nextIndex + 1 === scrollData.numOfScroll) setDisabledNext(true);
    } else if (infinite) {
      scrollDataRef.current = {
        ...scrollData,
        index: direction === -1 ? scrollData.numOfScroll - 1 : 0,
        sliding: true,
      };
    }

    if (!infinite && direction === -1) setDisabledNext(false);
    if (!infinite && direction === 1) setDisabledPrev(false);
    scroll();
  };

  return (
    <ArrowButtons
      className={className}
      disabledPrev={disabledPrev}
      disabledNext={disabledNext}
      onMove={move}
    />
  );
};

type SharedSectionProps = Pick<
  Props,
  'perSlide' | 'renderTitle' | 'scrollContainerClassName' | 'btnContainerClassName' | 'isArrowBottom'
>;

type StandardProps = SharedSectionProps &
  Pick<Props, 'totalCount' | 'numRecord' | 'infinite' | 'hasBlankCard'>;

const StandardScrollableSection: React.ReactFCWithChildren<StandardProps> = ({
  children,
  totalCount,
  numRecord,
  perSlide = 3,
  renderTitle,
  infinite = true,
  hasBlankCard,
  scrollContainerClassName,
  btnContainerClassName,
  isArrowBottom,
}) => {
  const isMounted = useIsMounted();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollDataRef = useRef<ScrollData>();
  const slideCount = Math.ceil(numRecord / perSlide);
  const numBlankCards = hasBlankCard ? 1 : 0;
  const numOfCards = totalCount > numRecord ? numRecord + numBlankCards : numRecord;

  useEffect(() => {
    if (!isMounted) return;

    const container = containerRef.current;
    const card = container?.querySelector<HTMLElement>('.js-card-scrollable');
    if (!container || !card) return;

    const containerWidth = container.parentElement?.offsetWidth || container.clientWidth;
    scrollDataRef.current = {
      index: 0,
      cardWidth: card.offsetWidth,
      containerWidth,
      numOfScroll: Math.ceil((card.offsetWidth * numOfCards) / containerWidth),
      sliding: false,
    };
  }, [isMounted, numOfCards, perSlide]);

  return (
    <SectionLayout
      renderTitle={renderTitle}
      containerRef={containerRef}
      containerClassName={`${
        scrollContainerClassName || 'tw-overflow-x-auto'
      } tw-overflow-y-hidden hide-scrollbar`}
      showArrowButtons={slideCount > 1}
      isArrowBottom={isArrowBottom}
      arrowButtons={
        <StandardArrowButtons
          className={twMerge('tw-mb-8', btnContainerClassName)}
          containerRef={containerRef}
          scrollDataRef={scrollDataRef}
          numOfCards={numOfCards}
          perSlide={perSlide}
          infinite={infinite}
        />
      }
    >
      {isValidElement(children) ? children : <div className="tw-flex tw-flex-nowrap">{children}</div>}
    </SectionLayout>
  );
};

type CenterProps = SharedSectionProps &
  Pick<Props, 'centerPadding' | 'initialIndex'> & {
    children: ReactNode;
    childCards: ReactNode[];
  };

const CenterModeScrollableSection: React.FC<CenterProps> = ({
  children,
  childCards,
  perSlide = 3,
  renderTitle,
  centerPadding,
  initialIndex,
  scrollContainerClassName,
  btnContainerClassName,
  isArrowBottom,
}) => {
  const isMounted = useIsMounted();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const currentIndexRef = useRef(0);

  const rafRef = useRef<number>();
  const animationRef = useRef<number>();
  const lastScrollLeftRef = useRef(Number.NaN);
  const idleFramesRef = useRef(0);

  const numOfCards = childCards.length;
  const fitsWithoutScroll = numOfCards > 0 && numOfCards <= perSlide;

  const slideCount = fitsWithoutScroll
    ? numOfCards
    : Math.max(numOfCards, perSlide + 2 * (RUNWAY_SLIDES + 1));
  const contentCount = Math.min(
    slideCount,
    Math.max(numOfCards, perSlide + 2 * (OVERSCAN_SLIDES + 1))
  );
  const middle = Math.floor(slideCount / 2);
  const safeInitialIndex = Math.min(Math.max(initialIndex ?? 0, 0), numOfCards - 1);
  const initialStart = fitsWithoutScroll
    ? 0
    : safeInitialIndex + Math.floor((perSlide - 1) / 2) - middle;
  const [start, setStart] = useState(initialStart);
  const [focus, setFocus] = useState(middle);
  const contentStart = Math.min(
    Math.max(focus - Math.floor(contentCount / 2), 0),
    slideCount - contentCount
  );

  const getCards = useCallback(() => {
    const container = containerRef.current;
    const cards = container
      ? Array.from(container.querySelectorAll<HTMLElement>('.js-center-scrollable-slide'))
      : [];

    return { container, cards };
  }, []);

  const getMetrics = useCallback(() => {
    const { container, cards } = getCards();
    if (!container || numOfCards === 0 || cards.length === 0) return null;

    const containerRect = container.getBoundingClientRect();
    const offsetOf = (el: HTMLElement) =>
      container.scrollLeft + el.getBoundingClientRect().left - containerRect.left;

    return { container, cards, offsetOf };
  }, [getCards, numOfCards]);

  const sizeCards = useCallback(() => {
    const { container, cards } = getCards();
    if (!container || cards.length === 0) return false;

    const containerWidth = container.clientWidth;
    const padding = parseCenterPadding(centerPadding, containerWidth);
    const cardWidth = Math.max((containerWidth - padding * 2) / perSlide, 0);

    cards.forEach((card) => applyCardSize(card, cardWidth));

    const spacerWidth = Math.max((containerWidth - cardWidth) / 2, 0);
    container.style.setProperty('--scrollable-center-spacer-width', `${spacerWidth}px`);
    container.style.scrollPaddingLeft = `${spacerWidth}px`;
    return true;
  }, [centerPadding, getCards, perSlide]);

  const getSlideTarget = useCallback(
    (index: number) => {
      const metrics = getMetrics();
      if (!metrics) return null;

      const { container, cards, offsetOf } = metrics;
      const safeIndex = Math.min(Math.max(index, 0), cards.length - 1);
      const card = cards[safeIndex];
      if (!card) return null;

      const spacer = Math.max((container.clientWidth - card.offsetWidth) / 2, 0);
      return { container, index: safeIndex, left: offsetOf(card) - spacer };
    },
    [getMetrics]
  );

  const stopAnimation = useCallback(() => {
    if (animationRef.current !== undefined) window.cancelAnimationFrame(animationRef.current);
    animationRef.current = undefined;

    const container = containerRef.current;
    if (container) container.style.scrollSnapType = SNAP_TYPE;
  }, []);

  const jumpToSlide = useCallback(
    (index: number) => {
      const target = getSlideTarget(index);
      if (!target) return;

      stopAnimation();
      currentIndexRef.current = target.index;
      target.container.scrollLeft = target.left;
    },
    [getSlideTarget, stopAnimation]
  );

  const animateToSlide = useCallback(
    (index: number) => {
      const target = getSlideTarget(index);
      if (!target) return;

      const { container } = target;
      const from = container.scrollLeft;
      const change = target.left - from;
      currentIndexRef.current = target.index;
      if (Math.abs(change) < 1) return;

      const duration = Math.min(340, 140 + Math.abs(change) * 0.6);
      const startTime = performance.now();

      stopAnimation();
      container.style.scrollSnapType = 'none';

      const step = (now: number) => {
        const progress = Math.min((now - startTime) / duration, 1);
        container.scrollLeft = from + change * (1 - (1 - progress) ** 3);

        if (progress < 1) {
          animationRef.current = window.requestAnimationFrame(step);
          return;
        }

        animationRef.current = undefined;
        container.style.scrollSnapType = SNAP_TYPE;
      };

      animationRef.current = window.requestAnimationFrame(step);
    },
    [getSlideTarget, stopAnimation]
  );

  const getClosest = useCallback(() => {
    const container = containerRef.current;
    const first = container?.querySelector<HTMLElement>('.js-center-scrollable-slide');
    const { left, width } = first?.getBoundingClientRect() ?? { left: 0, width: 0 };
    if (!container || !width || numOfCards === 0) {
      return { index: currentIndexRef.current, distance: 0, cardWidth: 0 };
    }

    const containerCenter = container.getBoundingClientRect().left + container.clientWidth / 2;
    const position = (containerCenter - left - width / 2) / width;
    const index = Math.min(Math.max(Math.round(position), 0), slideCount - 1);

    return { index, distance: Math.abs(position - index) * width, cardWidth: width };
  }, [numOfCards, slideCount]);

  const recenter = useCallback(
    (index: number) => {
      const shift = index - middle;
      const { container, cards } = getCards();
      const card = cards[index];
      if (!shift || !container || !card) return index;

      const left = card.getBoundingClientRect().left;
      flushSync(() => {
        setStart((value) => value + shift);
        setFocus(middle);
      });
      sizeCards();
      container.scrollLeft += getCards().cards[middle].getBoundingClientRect().left - left;
      return middle;
    },
    [getCards, middle, sizeCards]
  );

  const settle = useCallback(() => {
    const { index, distance, cardWidth } = getClosest();
    const normalizedIndex = recenter(index);
    currentIndexRef.current = normalizedIndex;

    if (cardWidth > 0 && distance > cardWidth * RESCUE_RATIO) animateToSlide(normalizedIndex);
  }, [animateToSlide, getClosest, recenter]);

  const monitor = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      rafRef.current = undefined;
      return;
    }

    const scrollLeft = container.scrollLeft;
    const moved = Math.abs(scrollLeft - lastScrollLeftRef.current) >= 0.5;
    lastScrollLeftRef.current = scrollLeft;
    idleFramesRef.current = moved ? 0 : idleFramesRef.current + 1;
    setFocus(getClosest().index);

    if (idleFramesRef.current >= IDLE_FRAMES) {
      rafRef.current = undefined;
      if (animationRef.current === undefined) settle();
      return;
    }

    rafRef.current = window.requestAnimationFrame(monitor);
  }, [getClosest, settle]);

  const startMonitor = useCallback(() => {
    if (rafRef.current !== undefined) return;

    lastScrollLeftRef.current = Number.NaN;
    idleFramesRef.current = 0;
    rafRef.current = window.requestAnimationFrame(monitor);
  }, [monitor]);

  const move = useCallback(
    (direction: Direction) => {
      if (numOfCards === 0) return;

      animateToSlide(recenter(getClosest().index) + direction * perSlide);
      startMonitor();
    },
    [animateToSlide, getClosest, numOfCards, perSlide, recenter, startMonitor]
  );

  useEffect(() => {
    const { container } = getCards();
    if (!container) return;

    const handleScroll = () => startMonitor();
    const handleResize = () => {
      sizeCards();
      jumpToSlide(currentIndexRef.current);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('touchstart', stopAnimation, { passive: true });
    container.addEventListener('wheel', stopAnimation, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('touchstart', stopAnimation);
      container.removeEventListener('wheel', stopAnimation);
      window.removeEventListener('resize', handleResize);

      if (rafRef.current !== undefined) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = undefined;
      if (animationRef.current !== undefined) window.cancelAnimationFrame(animationRef.current);
      animationRef.current = undefined;
    };
  }, [getCards, jumpToSlide, sizeCards, startMonitor, stopAnimation]);

  useEffect(() => {
    if (!isMounted || numOfCards === 0 || !sizeCards()) return;

    window.requestAnimationFrame(() => {
      flushSync(() => {
        setStart(initialStart);
        setFocus(middle);
      });
      sizeCards();
      jumpToSlide(middle);
    });
  }, [initialStart, isMounted, jumpToSlide, middle, numOfCards, sizeCards]);

  const cardContents = useMemo(
    () =>
      childCards.map((card) =>
        isValidElement(card)
          ? cloneElement(card, {
              className: classNames(card.props.className, 'tw-w-full tw-max-w-full'),
              ...(card.props.colClass
                ? {
                    colClass: classNames(card.props.colClass, 'tw-w-full tw-max-w-full'),
                  }
                : {}),
              style: {
                ...card.props.style,
                width: '100%',
                minWidth: '100%',
                maxWidth: '100%',
                flexBasis: '100%',
              },
            })
          : card
      ),
    [childCards]
  );

  const renderedChildren = useMemo(() => {
    const cardElements = Array.from({ length: slideCount }, (_, index) => {
      const offset = index - contentStart;

      return (
        <div
          key={start + index}
          className="js-center-scrollable-slide tw-flex-shrink-0"
          aria-hidden={offset >= 0 && offset < numOfCards ? undefined : 'true'}
        >
          {offset >= 0 && offset < contentCount
            ? cardContents[(((start + index) % numOfCards) + numOfCards) % numOfCards]
            : null}
        </div>
      );
    });

    const centerSlides = fitsWithoutScroll
      ? cardElements
      : [
          <div
            key="center-spacer-start"
            aria-hidden="true"
            className="tw-flex-shrink-0"
            style={{ width: 'var(--scrollable-center-spacer-width, 0px)' }}
          />,
          ...cardElements,
          <div
            key="center-spacer-end"
            aria-hidden="true"
            className="tw-flex-shrink-0"
            style={{ width: 'var(--scrollable-center-spacer-width, 0px)' }}
          />,
        ];

    return isValidElement(children) ? (
      cloneElement(children, { children: centerSlides })
    ) : (
      <div className="tw-flex tw-flex-nowrap">{centerSlides}</div>
    );
  }, [
    cardContents,
    children,
    contentCount,
    contentStart,
    fitsWithoutScroll,
    numOfCards,
    slideCount,
    start,
  ]);

  return (
    <SectionLayout
      renderTitle={renderTitle}
      containerRef={containerRef}
      containerClassName={classNames(
        fitsWithoutScroll ? 'tw-overflow-x-hidden' : 'tw-overflow-x-auto',
        'tw-overflow-y-hidden hide-scrollbar',
        { 'tw-flex tw-justify-center': fitsWithoutScroll },
        scrollContainerClassName
      )}
      containerStyle={{
        scrollSnapType: SNAP_TYPE,
        scrollBehavior: 'auto',
        overscrollBehaviorX: 'contain',
      }}
      showArrowButtons={numOfCards > perSlide}
      isArrowBottom={isArrowBottom}
      arrowButtons={
        <ArrowButtons
          className={twMerge('tw-mb-8', btnContainerClassName)}
          disabledPrev={false}
          disabledNext={false}
          onMove={move}
        />
      }
    >
      {renderedChildren}
    </SectionLayout>
  );
};

const ScrollableSection: React.ReactFCWithChildren<Props> = ({
  children,
  perSlide = 3,
  centerMode,
  responsive,
  ...props
}) => {
  const responsiveSettings = useResponsiveSettings(responsive);
  const activePerSlide = responsiveSettings.perSlide ?? perSlide;
  const activeCenterMode = responsiveSettings.centerMode ?? centerMode;
  const mergedProps = { ...props, ...responsiveSettings };

  const childCards = useMemo(
    () => Children.toArray(isValidElement(children) ? children.props.children : children),
    [children]
  );

  return activeCenterMode && childCards.length > 0 ? (
    <CenterModeScrollableSection {...mergedProps} childCards={childCards} perSlide={activePerSlide}>
      {children}
    </CenterModeScrollableSection>
  ) : (
    <StandardScrollableSection {...mergedProps} perSlide={activePerSlide}>
      {children}
    </StandardScrollableSection>
  );
};

export default ScrollableSection;
